import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { IoRocket } from 'react-icons/io5';
import { FiMail, FiUser, FiInfo, FiCheckCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const Login = () => {
  const [view, setView] = useState('login'); // login, join, success
  const [formData, setFormData] = useState({ username: '', password: '', name: '', email: '', type: 'individual' });
  const { login, requestToJoin } = useApp();
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');
    if (login(formData.username, formData.password)) {
      if (formData.username === 'admin') navigate('/admin');
      else navigate('/dashboard');
    } else {
      setError('Invalid credentials. If you just applied, please wait for Mission Control approval.');
    }
  };

  const handleJoin = (e) => {
    e.preventDefault();
    requestToJoin({ name: formData.name, email: formData.email, type: formData.type });
    setView('success');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 galaxy-bg">
      <div className="w-full max-w-md bg-slate-900/90 border border-slate-800 p-8 rounded-2xl shadow-2xl backdrop-blur-md relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600" />
        
        <div className="flex justify-center mb-6">
          <div className="p-3 bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20">
            <IoRocket className="text-white" size={32} />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {view === 'login' && (
            <motion.div
              key="login"
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 20, opacity: 0 }}
            >
              <h2 className="text-2xl font-black text-center mb-1 text-white uppercase tracking-tight">Access Capsule</h2>
              <p className="text-center text-slate-400 mb-8 text-sm">Enter the Maysalward impact ecosystem.</p>
              
              <form onSubmit={handleLogin} className="space-y-4">
                <Input 
                  icon={FiUser} 
                  placeholder="Username" 
                  value={formData.username} 
                  onChange={v => setFormData({ ...formData, username: v })} 
                />
                <Input 
                  icon={FiInfo} 
                  type="password" 
                  placeholder="Password" 
                  value={formData.password} 
                  onChange={v => setFormData({ ...formData, password: v })} 
                />
                {error && <p className="text-red-400 text-xs text-center font-medium">{error}</p>}
                <button className="w-full bg-blue-600 py-3 rounded-lg font-black text-white hover:bg-blue-500 transition-all uppercase tracking-widest text-sm">
                  Launch Identity
                </button>
              </form>

              <p className="mt-6 text-center text-slate-500 text-sm">
                New contributor? <button onClick={() => setView('join')} className="text-blue-400 font-bold hover:underline">Apply for Entry</button>
              </p>
            </motion.div>
          )}

          {view === 'join' && (
            <motion.div
              key="join"
              initial={{ x: 20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -20, opacity: 0 }}
            >
              <h2 className="text-2xl font-black text-center mb-1 text-white uppercase tracking-tight">Apply for Entry</h2>
              <p className="text-center text-slate-400 mb-8 text-sm">Join the Capsule community.</p>

              <form onSubmit={handleJoin} className="space-y-4">
                <Input 
                  icon={FiUser} 
                  placeholder="Full Name / Entity Name" 
                  value={formData.name} 
                  onChange={v => setFormData({ ...formData, name: v })} 
                />
                <Input 
                  icon={FiMail} 
                  placeholder="Email Address" 
                  value={formData.email} 
                  onChange={v => setFormData({ ...formData, email: v })} 
                />
                <div className="grid grid-cols-2 gap-2">
                  {['individual', 'institution'].map(t => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: t })}
                      className={`p-2.5 rounded-lg border text-[10px] font-black uppercase tracking-widest transition-all ${
                        formData.type === t 
                          ? 'bg-blue-600 border-blue-500 text-white' 
                          : 'bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-600'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                <button className="w-full bg-purple-600 py-3 rounded-lg font-black text-white hover:bg-purple-500 transition-all uppercase tracking-widest text-sm">
                  Request Access
                </button>
              </form>

              <p className="mt-6 text-center text-slate-500 text-sm">
                Already registered? <button onClick={() => setView('login')} className="text-blue-400 font-bold hover:underline">Login</button>
              </p>
            </motion.div>
          )}

          {view === 'success' && (
            <motion.div
              key="success"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center py-8"
            >
              <FiCheckCircle size={64} className="text-green-500 mx-auto mb-6" />
              <h2 className="text-2xl font-black text-white mb-2 uppercase tracking-tight">Signal Sent</h2>
              <p className="text-slate-400 text-sm mb-8 leading-relaxed">
                Your application has been beamed to Mission Control. We will contact you at <b>{formData.email}</b> once your orbit is cleared.
              </p>
              <button 
                onClick={() => setView('login')}
                className="bg-slate-800 text-white px-10 py-3 rounded-full font-bold hover:bg-slate-700 transition-all"
              >
                Back to Entry
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const Input = ({ icon: Icon, type = "text", placeholder, value, onChange }) => (
  <div className="relative">
    <Icon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
    />
  </div>
);

export default Login;